import { rangeSchema } from '@src/components/Home/Config/ConfigurableModes/validation/templateSchema';
import { ControlModeTemplateFieldsType } from '@src/types/ControlModes';
import * as yup from 'yup';

const fieldSchema = yup.object().shape({
  uiLabel: yup.string().required('UI Label is required'),
  controllerField: yup.string().required('Controller Field is required'),
  type: yup
    .string()
    .oneOf(
      Object.values(ControlModeTemplateFieldsType),
      'Type must be one of: string, number, boolean, select',
    )
    .required('Type is required'),
  range: yup.mixed().when('type', {
    is: ControlModeTemplateFieldsType.NUMBER,
    then: () => rangeSchema,
    otherwise: () => yup.mixed().nullable(),
  }),
  listOptions: yup.mixed().when('type', {
    is: ControlModeTemplateFieldsType.SELECT,
    then: () =>
      yup
        .array()
        .of(yup.string())
        .min(1, 'At least one option is required for select fields')
        .required('List options are required for select fields'),
    otherwise: () => yup.mixed().nullable(),
  }),
  value: yup
    .mixed()
    .test('valid-value', 'Value must match the field type', function (value) {
      if (value === undefined || value === null) {
        return true;
      }

      const { type } = this.parent;

      if (
        type === ControlModeTemplateFieldsType.STRING ||
        type === ControlModeTemplateFieldsType.SELECT
      ) {
        if (typeof value !== 'string') {
          return this.createError({
            message: `Value must be a string for ${type} type, but received ${typeof value}`,
          });
        }
        return true;
      }

      if (type === ControlModeTemplateFieldsType.NUMBER) {
        if (
          typeof value !== 'number' &&
          !(typeof value === 'string' && !isNaN(Number(value)))
        ) {
          return this.createError({
            message: `Value must be a number or a numeric string for ${type} type, but received ${typeof value}`,
          });
        }
        return true;
      }

      if (type === ControlModeTemplateFieldsType.BOOLEAN) {
        if (
          typeof value !== 'boolean' &&
          value !== 'true' &&
          value !== 'false'
        ) {
          return this.createError({
            message: `Value must be a boolean or 'true'/'false' string for ${type} type, but received ${typeof value === 'string' ? `string "${value}"` : typeof value}`,
          });
        }
        return true;
      }

      return true;
    })
    .test(
      'valid-select-value',
      'Value must be one of the list options',
      function (value) {
        if (value === undefined || value === null) {
          return true;
        }

        const { type, listOptions } = this.parent;

        if (
          type === ControlModeTemplateFieldsType.SELECT &&
          listOptions &&
          listOptions.length > 0
        ) {
          if (!listOptions.includes(String(value))) {
            return this.createError({
              message: `Value "${value}" is not in the list of available options: [${listOptions.join(', ')}]`,
            });
          }
          return true;
        }

        return true;
      },
    )
    .test(
      'valid-number-range',
      'Value must be within the specified range',
      function (value) {
        if (value === undefined || value === null) {
          return true;
        }

        const { type, range } = this.parent;

        if (type === ControlModeTemplateFieldsType.NUMBER && range) {
          const numValue = Number(value);
          const { min, max } = range;

          if (min !== undefined && min !== null && numValue < min) {
            return this.createError({
              message: `Value must be greater than or equal to ${min}`,
            });
          }

          if (max !== undefined && max !== null && numValue > max) {
            return this.createError({
              message: `Value must be less than or equal to ${max}`,
            });
          }
        }

        return true;
      },
    ),
  quickConfig: yup.boolean().required('Quick config flag is required'),
});

const sectionSchema = yup.object().shape({
  uiLabel: yup.string().required('UI Label is required'),
  controllerField: yup.string().required('Controller Field is required'),
  fields: yup
    .array()
    .of(fieldSchema)
    .required('Fields array is required')
    .min(1, 'At least one field is required')
    .test(
      'unique-controller-fields',
      'Controller fields must be unique within a section',
      function (fields) {
        if (!fields) {
          return true;
        }

        const controllerFields = fields.map((field) => field.controllerField);
        const uniqueFields = new Set(controllerFields);

        if (uniqueFields.size !== controllerFields.length) {
          return this.createError({
            message: 'Controller fields must be unique within a section',
          });
        }

        return true;
      },
    ),
});

const controlModeSchema = yup.object().shape({
  name: yup.string().required('Control mode name is required'),
  enabled: yup.boolean().required('Enabled flag is required'),
  sections: yup
    .array()
    .of(sectionSchema)
    .required('Sections array is required')
    .min(1, 'At least one section is required')
    .test(
      'unique-controller-fields',
      'Controller fields must be unique across all sections',
      function (sections) {
        if (!sections) {
          return true;
        }

        const controllerFields = sections.map(
          (section) => section.controllerField,
        );
        const uniqueFields = new Set(controllerFields);

        if (uniqueFields.size !== controllerFields.length) {
          return this.createError({
            message: 'Controller fields must be unique across all sections',
          });
        }

        return true;
      },
    ),
});

export const stackSchema = yup.object().shape({
  name: yup.string().required('Stack name is required'),
  controlModes: yup
    .array()
    .of(controlModeSchema)
    .required('Control modes are required')
    .min(1, 'At least one control mode is required'),
});

export const stacksArraySchema = yup
  .array()
  .of(stackSchema)
  .required('Stacks array is required')
  .min(1, 'At least one stack is required');
